import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import ConfirmationModal from 'components/PreferenceDialog/Success';
import ErrorModal from 'components/PreferenceDialog/Error';
import SavePreferencesModal from 'components/Meteorology/Fields/ControlPanel/Comparator/SavePreferencesModal';
import {
  useDeleteComparatorPreferencesMutation,
  useListComparatorPreferencesQuery,
} from 'services/api/Meteorology';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import usePopularComparisons from 'utils/hooks/meteorology/usePopularComparisons';
import useSetComparison from 'utils/hooks/meteorology/comparator/useSetComparison';
import useComparatorSavePreferencesModal from 'utils/hooks/meteorology/useComparatorSavePreferencesModal';
import {
  selectMFComparatorControlPanelSubtitle,
  selectMFComparatorControlPanelTitle,
  setTriggerActivity,
} from 'redux/reducers/meteorologyFieldsComparatorControlPanelReducer';
import { Comparison } from 'helper/products/meteorology';
import ComparisonList from './ComparisonList';
import NovaForm from './NovaForm';

import './index.scss';

export type ControlPanelSheetTab = 'comparacoes' | 'preferencias' | 'nova';

export default function ControlPanelSheet({
  open,
  dismiss,
  defaultTab = 'comparacoes',
}: {
  open: boolean;
  dismiss: () => void;
  defaultTab?: ControlPanelSheetTab;
}) {
  const dispatch = useDispatch();
  const [tab, setTab] = useState<ControlPanelSheetTab>(defaultTab);

  useEffect(() => {
    if (open) setTab(defaultTab);
  }, [open, defaultTab]);

  const { comparisons, isFetching: comparisonsFetching } = usePopularComparisons();
  const { data, error } = useListComparatorPreferencesQuery();
  const preferences = data?.data || [];
  const [deleteTrigger, { isLoading: deleteLoading, error: deleteError }] = (
    useDeleteComparatorPreferencesMutation()
  );
  useHandleRequestErrors(error as ResponseError | undefined);
  useHandleRequestErrors(deleteError as ResponseError | undefined);

  const setComparison = useSetComparison();
  const title = useSelector(selectMFComparatorControlPanelTitle);
  const subtitle = useSelector(selectMFComparatorControlPanelSubtitle);

  const {
    isShowingConfirmationModal,
    setShowConfirmationModal,
    isShowingErrorModal,
    setShowErrorModal,
    error: saveError,
  } = useComparatorSavePreferencesModal();

  const selectComparison = (groupTitle: string) => (item: Comparison) => {
    setComparison(item, groupTitle);
    dispatch(setTriggerActivity(true));
    dismiss();
  };

  const isSelected = (groupTitle: string) => (item: Comparison) => (
    title === groupTitle && subtitle === item.name
  );

  return (
    <>
      <ConfirmationModal
        open={isShowingConfirmationModal}
        closing={() => setShowConfirmationModal(false)}
      />
      <ErrorModal
        open={isShowingErrorModal}
        message={saveError}
        closing={() => setShowErrorModal(false)}
      />
      <SavePreferencesModal />
      <BottomSheet
        open={open}
        dismiss={dismiss}
        title="Painel de controle"
        className="control-panel-sheet"
      >
        <div className="control-panel-sheet-tabs">
          <button
            type="button"
            className={`control-panel-sheet-tab${tab === 'comparacoes' ? ' active' : ''}`}
            onClick={() => setTab('comparacoes')}
          >
            Comparações
          </button>
          <button
            type="button"
            className={`control-panel-sheet-tab${tab === 'preferencias' ? ' active' : ''}`}
            onClick={() => setTab('preferencias')}
          >
            Preferências
          </button>
          <button
            type="button"
            className={`control-panel-sheet-tab${tab === 'nova' ? ' active' : ''}`}
            onClick={() => setTab('nova')}
          >
            Nova
          </button>
        </div>

        {tab === 'comparacoes' && (
          <ComparisonList
            items={comparisons}
            isFetching={comparisonsFetching}
            isSelected={isSelected('Comparações Populares')}
            onSelect={selectComparison('Comparações Populares')}
            emptyMessage="Nenhuma comparação popular disponível"
          />
        )}
        {tab === 'preferencias' && (
          <ComparisonList
            items={preferences}
            isFetching={false}
            isSelected={isSelected('Preferências Salvas')}
            onSelect={selectComparison('Preferências Salvas')}
            onDelete={(item) => deleteTrigger({ id: item.id })}
            deleteLoading={deleteLoading}
            emptyMessage="Nenhuma preferência salva"
          />
        )}
        {tab === 'nova' && <NovaForm onGenerate={dismiss} />}
      </BottomSheet>
    </>
  );
}
